import { Link } from "react-router-dom";
import type { BlogPost } from "../../../mocks/blog";

const categoryColors: Record<string, string> = {
  "Product Sourcing": "bg-amber-50 text-amber-700",
  "Wholesale": "bg-[#D9E2EC] text-[#3A506B]",
  "Inventory": "bg-teal-50 text-teal-700",
  "Logistics": "bg-slate-100 text-slate-600",
  "Supply Chain": "bg-[#C9A227]/10 text-[#C9A227]",
};

interface BlogCardProps {
  post: BlogPost;
  variant?: "default" | "featured";
}

export default function BlogCard({ post, variant = "default" }: BlogCardProps) {
  const catStyle = categoryColors[post.category] ?? "bg-gray-100 text-gray-600";

  if (variant === "featured") {
    return (
      <Link
        to={`/blog/${post.slug}`}
        className="group grid grid-cols-1 lg:grid-cols-2 rounded-2xl overflow-hidden border border-[#E8ECF0] bg-white hover:-translate-y-0.5 transition-transform duration-300 cursor-pointer"
      >
        {/* Image */}
        <div className="relative w-full h-64 lg:h-auto overflow-hidden">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent"></div>
          <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full bg-[#C9A227] text-white">
            Featured
          </span>
        </div>

        {/* Content */}
        <div className="p-8 lg:p-10 flex flex-col justify-between">
          <div>
            <span className={`inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4 ${catStyle}`}>
              {post.category}
            </span>
            <h2 className="text-2xl lg:text-3xl font-extrabold text-[#222831] leading-tight mb-4 group-hover:text-[#C9A227] transition-colors">
              {post.title}
            </h2>
            <p className="text-sm text-[#3A506B] leading-relaxed line-clamp-3">{post.excerpt}</p>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-full text-white text-xs font-bold ${post.author.bgColor}`}>
                {post.author.initials}
              </div>
              <div>
                <p className="text-xs font-semibold text-[#222831]">{post.author.name}</p>
                <p className="text-xs text-[#3A506B]/60">{post.date}</p>
              </div>
            </div>
            <span className="text-xs text-[#3A506B]/60 flex items-center gap-1">
              <i className="ri-time-line text-sm"></i>
              {post.readTime} min read
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col rounded-2xl overflow-hidden border border-[#E8ECF0] bg-white hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
    >
      {/* Image */}
      <div className="w-full h-48 overflow-hidden">
        <img
          src={post.coverImage}
          alt={post.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-6">
        <span className={`self-start text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3 ${catStyle}`}>
          {post.category}
        </span>
        <h3 className="text-base font-bold text-[#222831] leading-snug mb-2 group-hover:text-[#C9A227] transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="text-sm text-[#3A506B] leading-relaxed line-clamp-2 flex-1">{post.excerpt}</p>

        {/* Footer */}
        <div className="mt-5 pt-4 border-t border-[#F0F2F5] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 flex items-center justify-center rounded-full text-white text-xs font-bold ${post.author.bgColor}`}>
              {post.author.initials}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#222831] leading-none">{post.author.name}</p>
              <p className="text-xs text-[#3A506B]/50 mt-0.5">{post.date}</p>
            </div>
          </div>
          <span className="text-xs text-[#3A506B]/50 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-time-line text-xs"></i>
            {post.readTime} min
          </span>
        </div>
      </div>
    </Link>
  );
}